import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';
import {
  FileText, Activity, BarChart2, AlertTriangle, Droplets, TrendingUp,
} from '../components/Icons';
import { getStoolInfo } from '../utils/stoolInfo';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import styles from './Progress.module.css';

// Warna grafik = nilai token design system (recharts tak bisa baca CSS var)
const C = {
  teal: '#0a5252',
  tealMid: '#2f7a6e',
  tealLight: '#95c9bd',
  orange: '#e27830',
  red: '#dc2626',
  blue: '#2563eb',
  success: '#2f7a6e',
};
const PIE_COLORS = [C.teal, C.tealMid, C.tealLight, C.orange, C.blue, C.red];

function initial(name = '') {
  return (name.trim()[0] || '?').toUpperCase();
}

function StatCard({ icon, label, value, toneClass }) {
  return (
    <div className={`${styles.statCard} ${toneClass}`}>
      <div className={styles.statIcon}>{icon}</div>
      <div className={styles.statInfo}>
        <div className={styles.statLabel}>{label}</div>
        <div className={styles.statValue}>{value}</div>
      </div>
    </div>
  );
}

function Progress() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isID = i18n.language === 'id';

  const [children, setChildren] = useState([]);
  const [activeChildId, setActiveChildId] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      const { data } = await supabase.from('children').select('id, name')
        .eq('caregiver_id', user.id).order('created_at', { ascending: false });
      if (active && data && data.length > 0) {
        setChildren(data);
        setActiveChildId(data[0].id);
      }
    })();
    return () => { active = false; };
  }, [user.id]);

  const fetchLogs = useCallback(async () => {
    if (!activeChildId) return;
    setLoading(true);
    const { data } = await supabase.from('gi_logs').select('*')
      .eq('caregiver_id', user.id)
      .eq('child_id', activeChildId)
      .order('date', { ascending: true });
    setLogs(data || []);
    setLoading(false);
  }, [user.id, activeChildId]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // ===== STATISTIK =====
  const avgStoolFreq = logs.length
    ? (logs.reduce((s, l) => s + (l.stool_frequency || 0), 0) / logs.length).toFixed(1)
    : '-';

  // Bristol paling umum — bug "Tipe undefined" diperbaiki dengan kurung
  const mostCommonStool = (() => {
    if (!logs.length) return '-';
    const freq = {};
    logs.forEach(l => { if (l.stool_type) freq[l.stool_type] = (freq[l.stool_type] || 0) + 1; });
    const top = Object.entries(freq).sort((a, b) => b[1] - a[1])[0];
    if (!top) return '-';
    const type = top[0];
    const info = getStoolInfo(type, isID);
    return `${isID ? 'Tipe' : 'Type'} ${type}`; // penjelasan ditaruh di subteks kartu
  })();

  const mostCommonInfo = (() => {
    if (!logs.length) return null;
    const freq = {};
    logs.forEach(l => { if (l.stool_type) freq[l.stool_type] = (freq[l.stool_type] || 0) + 1; });
    const top = Object.entries(freq).sort((a, b) => b[1] - a[1])[0];
    return top ? getStoolInfo(top[0], isID) : null;
  })();

  const badSmellCount = logs.filter(l => l.stool_smell === 'Menyengat').length;
  const poorUrination = logs.filter(l => l.urination === 'Tidak Lancar').length;

  // ===== DATA GRAFIK =====
  const bristolData = logs.map(l => ({
    date: l.date, bristol: l.stool_type || null, frekuensi: l.stool_frequency || 0,
  }));

  const appetiteScore = (val) => val === 'Baik' ? 3 : val === 'Kurang' ? 2 : val === 'Menolak' ? 1 : null;
  const mealData = logs.map(l => ({
    date: l.date,
    Pagi: appetiteScore(l.appetite_morning),
    Siang: appetiteScore(l.appetite_noon),
    Malam: appetiteScore(l.appetite_night),
  }));

  const colorCount = {};
  logs.forEach(l => { if (l.stool_color) colorCount[l.stool_color] = (colorCount[l.stool_color] || 0) + 1; });
  const colorData = Object.entries(colorCount).map(([name, value]) => ({ name, value }));

  const portionCount = { Banyak: 0, Cukup: 0, Sedikit: 0 };
  logs.forEach(l => {
    ['morning', 'noon', 'night'].forEach(m => {
      if (l[`portion_${m}`]) portionCount[l[`portion_${m}`]] = (portionCount[l[`portion_${m}`]] || 0) + 1;
    });
  });
  const portionData = Object.entries(portionCount).map(([name, value]) => ({ name, value }));

  const fluidOk = logs.reduce((s, l) =>
    s + ['morning', 'noon', 'night'].filter(m => l[`fluid_${m}`] === 'Cukup').length, 0);
  const fluidLow = logs.reduce((s, l) =>
    s + ['morning', 'noon', 'night'].filter(m => l[`fluid_${m}`] === 'Kurang').length, 0);
  const fluidData = [
    { name: isID ? 'Cukup' : 'Adequate', value: fluidOk },
    { name: isID ? 'Kurang' : 'Low', value: fluidLow },
  ];

  // ===== STATE: belum ada anak =====
  if (children.length === 0) {
    return (
      <div className={styles.page}>
        <h1 className={styles.title}>
          <TrendingUp size={24} aria-hidden="true" />
          {isID ? 'Visualisasi Progress' : 'Progress Visualization'}
        </h1>
        <div className={styles.emptyBox}>
          {isID ? 'Belum ada data anak. Tambahkan profil anak terlebih dahulu.'
                : 'No child data yet. Please add a child profile first.'}
        </div>
      </div>
    );
  }

  const activeChild = children.find(c => c.id === activeChildId);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>
            <TrendingUp size={24} aria-hidden="true" />
            {isID ? 'Visualisasi Progress' : 'Progress Visualization'}
          </h1>
          <p className={styles.subtitle}>
            {isID ? 'Pantau perkembangan kesehatan pencernaan anak dari waktu ke waktu'
                  : 'Track your child\u2019s GI health over time'}
          </p>
        </div>

        <div className={styles.childChips} role="group" aria-label={isID ? 'Pilih anak' : 'Select child'}>
          {children.map(c => {
            const on = c.id === activeChildId;
            return (
              <button key={c.id}
                className={`${styles.childChip} ${on ? styles.childChipActive : ''}`}
                aria-pressed={on}
                onClick={() => setActiveChildId(c.id)}>
                <span className={styles.childAvatar}>{initial(c.name)}</span>
                {c.name}
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <p className={styles.empty}>{t('common.loading')}</p>
      ) : logs.length === 0 ? (
        <div className={styles.emptyBox}>
          {isID ? `Belum ada catatan untuk ${activeChild?.name || 'anak ini'}.`
                : `No records yet for ${activeChild?.name || 'this child'}.`}
        </div>
      ) : (
        <>
          {/* STATISTIK */}
          <div className={styles.statGrid}>
            <StatCard toneClass={styles.toneTeal}
              icon={<FileText size={22} aria-hidden="true" />}
              label={isID ? 'Total Catatan' : 'Total Records'}
              value={`${logs.length} ${isID ? 'catatan' : 'logs'}`} />
            <StatCard toneClass={styles.toneGreen}
              icon={<Activity size={22} aria-hidden="true" />}
              label={isID ? 'Rata-rata Frekuensi BAB' : 'Avg Stool Frequency'}
              value={`${avgStoolFreq}x`} />
            <StatCard toneClass={styles.toneOrange}
              icon={<BarChart2 size={22} aria-hidden="true" />}
              label={isID ? 'Bentuk Feses Tersering' : 'Most Common Stool'}
              value={mostCommonStool} />
            <StatCard toneClass={styles.toneRed}
              icon={<AlertTriangle size={22} aria-hidden="true" />}
              label={isID ? 'Bau Menyengat' : 'Strong Odor'}
              value={`${badSmellCount}x`} />
            <StatCard toneClass={styles.toneBlue}
              icon={<Droplets size={22} aria-hidden="true" />}
              label={isID ? 'BAK Tidak Lancar' : 'Poor Urination'}
              value={`${poorUrination}x`} />
          </div>

          {/* GRAFIK 1: Bentuk feses & frekuensi */}
          <div className={styles.chartCard}>
            <h2 className={styles.sectionTitle}>
              <Activity size={18} aria-hidden="true" />
              {isID ? 'Bentuk Feses & Frekuensi BAB' : 'Stool Form & Frequency'}
              <span className={styles.chartTitleSub}>({isID ? 'Skala Bristol' : 'Bristol Scale'})</span>
            </h2>
            <p className={styles.chartDesc}>
              {isID
                ? 'Tipe 3–5 termasuk normal (Tipe 4 paling ideal). Tipe 1–2 cenderung sembelit, Tipe 6–7 cenderung diare.'
                : 'Types 3–5 are normal (Type 4 is ideal). Types 1–2 tend toward constipation, Types 6–7 toward diarrhea.'}
            </p>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={bristolData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5efe c" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="left" domain={[1, 7]} ticks={[1,2,3,4,5,6,7]} tick={{ fontSize: 11 }}
                  label={{ value: isID ? 'Bentuk' : 'Form', angle: -90, position: 'insideLeft', fontSize: 11 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }}
                  label={{ value: isID ? 'Frekuensi' : 'Frequency', angle: 90, position: 'insideRight', fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Line yAxisId="left" type="monotone" dataKey="bristol" stroke={C.teal} strokeWidth={2}
                  dot={{ r: 4 }} name={isID ? 'Bentuk feses' : 'Stool form'} connectNulls />
                <Line yAxisId="right" type="monotone" dataKey="frekuensi" stroke={C.orange} strokeWidth={2}
                  dot={{ r: 4 }} name={isID ? 'Frekuensi/hari' : 'Frequency/day'} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* GRAFIK 2: Nafsu makan */}
          <div className={styles.chartCard}>
            <h2 className={styles.sectionTitle}>
              <Activity size={18} aria-hidden="true" />
              {isID ? 'Nafsu Makan — Pagi, Siang & Malam' : 'Appetite — Morning, Noon & Night'}
            </h2>
            <p className={styles.chartDesc}>
              {isID ? 'Skor: 3 = Baik, 2 = Kurang, 1 = Menolak' : 'Score: 3 = Good, 2 = Low, 1 = Refused'}
            </p>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={mealData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5efec" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis domain={[1, 3]} ticks={[1, 2, 3]}
                  tickFormatter={v => v === 3 ? (isID ? 'Baik' : 'Good') : v === 2 ? (isID ? 'Kurang' : 'Low') : (isID ? 'Menolak' : 'Refused')}
                  tick={{ fontSize: 10 }} width={60} />
                <Tooltip formatter={(v) => v === 3 ? (isID ? 'Baik' : 'Good') : v === 2 ? (isID ? 'Kurang' : 'Low') : (isID ? 'Menolak' : 'Refused')} />
                <Legend />
                <Line type="monotone" dataKey="Pagi" stroke={C.teal} strokeWidth={2} dot={{ r: 3 }} connectNulls />
                <Line type="monotone" dataKey="Siang" stroke={C.tealMid} strokeWidth={2} dot={{ r: 3 }} connectNulls />
                <Line type="monotone" dataKey="Malam" stroke={C.tealLight} strokeWidth={2} dot={{ r: 3 }} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* GRAFIK 3 & 4: Pie + Bar */}
          <div className={styles.pieRow}>
            <div className={styles.chartCardHalf}>
              <h2 className={styles.sectionTitle}>
                <BarChart2 size={18} aria-hidden="true" />
                {isID ? 'Distribusi Warna Feses' : 'Stool Color Distribution'}
              </h2>
              {colorData.length === 0 ? (
                <p className={styles.empty}>{isID ? 'Belum ada data' : 'No data yet'}</p>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={colorData} cx="50%" cy="50%" outerRadius={80} dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                      {colorData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className={styles.chartCardHalf}>
              <h2 className={styles.sectionTitle}>
                <Activity size={18} aria-hidden="true" />
                {isID ? 'Distribusi Porsi Makan' : 'Meal Portion Distribution'}
              </h2>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={portionData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5efec" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="value" name={isID ? 'Jumlah' : 'Count'} radius={[6, 6, 0, 0]}>
                    {portionData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* GRAFIK 5: Cairan */}
          <div className={styles.chartCard}>
            <h2 className={styles.sectionTitle}>
              <Droplets size={18} aria-hidden="true" />
              {isID ? 'Asupan Cairan — Cukup vs Kurang' : 'Fluid Intake — Adequate vs Low'}
            </h2>
            <p className={styles.chartDesc}>
              {isID ? 'Total sesi makan (pagi+siang+malam) yang tercatat'
                    : 'Total meal sessions (morning+noon+night) recorded'}
            </p>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={fluidData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5efec" />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={70} />
                <Tooltip />
                <Bar dataKey="value" name={isID ? 'Jumlah sesi' : 'Sessions'} radius={[0, 6, 6, 0]}>
                  <Cell fill={C.success} />
                  <Cell fill={C.orange} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
}

export default Progress;